import { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  FileText,
  Globe,
  Loader2,
  Stethoscope,
  UserRound,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePermissions } from '../../hooks/usePermissions';
import { useSettings, useUpdateAdmission } from '../../services/httpServices/queries';
import {
  DEFAULT_HOSPITAL_TIMEZONE,
  datetimeLocalToISOInTimeZone,
  toDatetimeLocalValueInTimeZone,
} from '../../lib/timezones';
import { DateTimePicker } from './DatePicker';
import { DoctorSearchSelect } from './DoctorSearchSelect';
import {
  OutsideDoctorModal,
  type CreatedDoctor,
} from './outside-doctor-modal';

/** Which doctor field the quick "Add Outside Doctor" action was opened from. */
type OutsideDoctorTarget = 'doctor' | 'referrer';

type AdmissionTypeKey = 'DIRECT' | 'REFERRAL' | 'EMERGENCY';

interface AdmissionDetailsModalProps {
  open: boolean;
  admissionId: string;
  /** Primary (treating) doctor currently on the admission. */
  doctorId?: string | null;
  doctorName?: string | null;
  /** Current admission type as stored on the admission. */
  currentType?: string | null;
  /** Admission timestamp (ISO). Editable only with `ipd:admission:backdate`. */
  admittedAt?: string | null;
  /** Emergency stays keep their type; the rest can be corrected. */
  isEmergency: boolean;
  referredById?: string | null;
  referredByName?: string | null;
  onClose: () => void;
}

const TYPE_OPTIONS: {
  key: AdmissionTypeKey;
  label: string;
  description: string;
  icon: typeof ArrowRight;
}[] = [
  {
    key: 'DIRECT',
    label: 'Direct',
    description: 'Patient came to the hospital on their own',
    icon: ArrowRight,
  },
  {
    key: 'REFERRAL',
    label: 'Referral',
    description: 'Sent by a referring doctor or clinic',
    icon: FileText,
  },
  {
    key: 'EMERGENCY',
    label: 'Emergency',
    description: 'Registered without a bed — only at registration',
    icon: AlertCircle,
  },
];

/**
 * Edits the admission's administrative facts — primary doctor, admission type
 * and referring doctor — without leaving the admission screen. All three are
 * editable on a live admission only.
 *
 * The admission type is one-way: an emergency stay keeps its type, and a
 * regular stay can be switched between direct and referral but never to
 * emergency after registration (the backend enforces the same rules).
 */
export function AdmissionDetailsModal({
  open,
  admissionId,
  doctorId,
  doctorName,
  currentType,
  admittedAt,
  isEmergency,
  referredById,
  referredByName,
  onClose,
}: AdmissionDetailsModalProps) {
  const updateAdmission = useUpdateAdmission();

  // Only roles granted `ipd:admission:backdate` may see or change the admission
  // timestamp — the backend enforces the same gate on the update endpoint.
  const { hasPermission } = usePermissions();
  const canEditAdmissionTime = hasPermission('ipd:admission:backdate');

  // Hospital timezone configured on Settings → General (`hospital_timezone`),
  // matching how the new-admission form seeds and submits the timestamp.
  const { data: settingsData } = useSettings();
  const hospitalTimezone = useMemo(() => {
    const value = settingsData?.data?.find((s: any) => s.key === 'hospital_timezone')?.value;
    return (typeof value === 'string' ? value.trim() : '') || DEFAULT_HOSPITAL_TIMEZONE;
  }, [settingsData]);

  const [primaryDoctorId, setPrimaryDoctorId] = useState('');
  const [type, setType] = useState<AdmissionTypeKey>('DIRECT');
  const [referrerId, setReferrerId] = useState('');
  const [admittedAtValue, setAdmittedAtValue] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  // Doctors created inline through "+ Add Outside Doctor". Kept locally so the
  // new selection renders with its name before the doctors query refetches.
  const [outsideDoctors, setOutsideDoctors] = useState<CreatedDoctor[]>([]);
  const [showOutsideDoctorModal, setShowOutsideDoctorModal] = useState(false);
  const [outsideDoctorTarget, setOutsideDoctorTarget] =
    useState<OutsideDoctorTarget>('doctor');
  const [outsideDoctorQuery, setOutsideDoctorQuery] = useState('');

  const savedType = useMemo<AdmissionTypeKey>(() => {
    const value = String(currentType ?? '').toUpperCase();
    if (value === 'REFERRAL') return 'REFERRAL';
    if (value === 'EMERGENCY') return 'EMERGENCY';
    return 'DIRECT';
  }, [currentType]);

  const savedDoctorId = doctorId || '';
  const savedReferrerId = referredById || '';
  // The stored instant rendered as the picker's hospital-timezone wall clock,
  // so the field shows the same time the new-admission form would submit.
  const savedAdmittedAtLocal = useMemo(() => {
    if (!admittedAt) return '';
    const instant = new Date(admittedAt);
    if (Number.isNaN(instant.getTime())) return '';
    return toDatetimeLocalValueInTimeZone(instant, hospitalTimezone);
  }, [admittedAt, hospitalTimezone]);

  useEffect(() => {
    if (!open) return;
    setPrimaryDoctorId(savedDoctorId);
    setType(savedType);
    setReferrerId(savedReferrerId);
    setAdmittedAtValue(savedAdmittedAtLocal);
    setFormError(null);
    setOutsideDoctors([]);
    setShowOutsideDoctorModal(false);
    setOutsideDoctorQuery('');
  }, [open, savedDoctorId, savedType, savedReferrerId, savedAdmittedAtLocal]);

  if (!open) return null;

  // An emergency stay is final in both directions: it keeps its type, and no
  // other admission can become emergency after it is registered. The lock is
  // driven by the stored type, never by the current bed (an emergency stay keeps
  // its type even after a bed is assigned).
  const typeLocked = savedType === 'EMERGENCY';
  /** Emergency is only ever a registration-time choice. */
  const isTypeSelectable = (key: AdmissionTypeKey) =>
    key !== 'EMERGENCY' || savedType === 'EMERGENCY';
  const doctorChanged = primaryDoctorId !== savedDoctorId;
  const typeChanged = type !== savedType;
  const referrerChanged = referrerId !== savedReferrerId;
  const admittedAtChanged =
    canEditAdmissionTime && admittedAtValue !== savedAdmittedAtLocal;
  const hasChanges =
    doctorChanged || typeChanged || referrerChanged || admittedAtChanged;

  /** Label for a doctor created here, so the picker shows its name immediately. */
  const createdLabel = (id: string) =>
    outsideDoctors.find((doctor) => doctor.id === id)?.name;
  const primaryDoctorLabel = createdLabel(primaryDoctorId) || doctorName || undefined;
  const referrerLabel = createdLabel(referrerId) || referredByName || undefined;

  const openOutsideDoctor = (target: OutsideDoctorTarget, query: string) => {
    setOutsideDoctorTarget(target);
    setOutsideDoctorQuery(query);
    setShowOutsideDoctorModal(true);
  };

  /** Selects the doctor that was just created in the quick-entry dialog. */
  const handleOutsideDoctorCreated = (doctor: CreatedDoctor) => {
    setOutsideDoctors((prev) => [...prev, doctor]);
    if (outsideDoctorTarget === 'doctor') setPrimaryDoctorId(doctor.id);
    else setReferrerId(doctor.id);
  };

  const handleSubmit = async () => {
    if (!hasChanges) {
      onClose();
      return;
    }
    if (canEditAdmissionTime && !admittedAtValue) {
      setFormError('Admission date & time is required.');
      return;
    }
    if (typeChanged && type === 'EMERGENCY') {
      setFormError(
        'An admission cannot be changed to emergency after it is registered.',
      );
      return;
    }
    setFormError(null);
    try {
      await updateAdmission.mutateAsync({
        id: admissionId,
        data: {
          // An empty selection means "nobody" — send null so an existing
          // doctor is cleared instead of silently left in place.
          ...(doctorChanged ? { doctorId: primaryDoctorId || null } : {}),
          ...(typeChanged ? { admissionType: type } : {}),
          ...(referrerChanged ? { referredById: referrerId || null } : {}),
          // The picker's wall clock is interpreted in the hospital timezone,
          // exactly like the new-admission form, so the saved instant matches
          // what was entered. The backend also requires `ipd:admission:backdate`.
          ...(admittedAtChanged
            ? {
                admittedAt: datetimeLocalToISOInTimeZone(
                  admittedAtValue,
                  hospitalTimezone,
                ),
              }
            : {}),
        },
      });
      toast.success(
        admittedAtChanged ? 'Admission date updated — invoices re-dated' : 'Admission updated',
      );
      onClose();
    } catch (err: any) {
      setFormError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not update the admission.',
      );
    }
  };

  return (
    <>
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      onClick={() => {
        if (!updateAdmission.isPending) onClose();
      }}
    >
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-border bg-surface shadow-xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <div className="flex items-center gap-2.5">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Stethoscope size={17} />
            </span>
            <div>
              <h2 className="text-sm font-semibold text-text">
                Admission details
              </h2>
              <p className="text-[0.6875rem] text-text-muted">
                Admission date, doctor, type and referrer
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={updateAdmission.isPending}
            className="rounded-lg p-1.5 text-text-muted transition-colors hover:bg-background hover:text-text disabled:opacity-50"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 p-5">
          {formError && (
            <div className="flex items-start gap-2 rounded-lg border border-danger/20 bg-danger/10 px-3 py-2 text-sm text-danger">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{formError}</span>
            </div>
          )}

          {/* Backdating an admission is a sensitive action, gated exactly like
              the new-admission form (`ipd:admission:backdate`). Roles without
              the permission never see the field and the server stamps "now". */}
          {canEditAdmissionTime && (
            <div>
              <DateTimePicker
                label="Admission Date & Time *"
                value={admittedAtValue}
                onChange={setAdmittedAtValue}
                disabled={updateAdmission.isPending}
                timeZone={hospitalTimezone}
              />
              <p
                className="mt-1 flex items-center gap-1 text-[0.6875rem] leading-4 text-text-muted"
                title="Timezone from Settings → General"
              >
                <Globe size={10} className="shrink-0" />
                Times shown in {hospitalTimezone} (Settings → General). Saving a
                new date re-dates this stay&apos;s invoices to match.
              </p>
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-xs font-medium text-text">
              Primary doctor{' '}
              <span className="font-normal text-text-muted">· optional</span>
            </label>
            <DoctorSearchSelect
              value={primaryDoctorId}
              onChange={(value) => setPrimaryDoctorId(value)}
              placeholder="Select or add outside doctor..."
              disabled={updateAdmission.isPending}
              selectedLabel={primaryDoctorLabel}
              showPreview={false}
              onAddNew={(query) => openOutsideDoctor('doctor', query)}
              addNewLabel="Add Outside Doctor"
            />
            {createdLabel(primaryDoctorId) && (
              <p className="mt-1 flex items-center gap-1 text-[0.6875rem] text-success">
                <CheckCircle2 size={12} />
                Outside doctor added
              </p>
            )}
            <p className="mt-1.5 flex items-start gap-1 text-[0.6875rem] text-text-muted">
              <UserRound size={11} className="mt-0.5 shrink-0" />
              The doctor in charge of this stay. Clear the field to leave the
              admission without a primary doctor; charges already posted keep the
              doctor they were billed under.
            </p>
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-text">
              Admission type <span className="text-danger">*</span>
            </label>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
              {TYPE_OPTIONS.map((option) => {
                const Icon = option.icon;
                const active = type === option.key;
                // Emergency can only be chosen while the stay already *is* an
                // emergency — never for a regular admission.
                const selectable = isTypeSelectable(option.key);
                const disabled =
                  updateAdmission.isPending || typeLocked || !selectable;
                return (
                  <button
                    key={option.key}
                    type="button"
                    disabled={disabled}
                    aria-pressed={active}
                    title={
                      !selectable && !typeLocked
                        ? 'An admission cannot be changed to emergency after registration'
                        : typeLocked
                          ? 'An emergency admission keeps its admission type'
                          : option.description
                    }
                    onClick={() => setType(option.key)}
                    className={`flex items-start gap-2 rounded-xl border px-3 py-2 text-left transition-colors ${
                      active
                        ? 'border-primary bg-primary/10 text-primary'
                        : 'border-border bg-background text-text hover:border-primary/40'
                    } ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
                  >
                    <Icon size={15} className="mt-0.5 shrink-0" />
                    <span className="min-w-0">
                      <span className="block text-xs font-semibold">
                        {option.label}
                      </span>
                      <span className="block text-[0.625rem] leading-tight text-text-muted">
                        {option.description}
                      </span>
                    </span>
                  </button>
                );
              })}
            </div>
            {typeLocked && (
              <p className="mt-1.5 text-[0.6875rem] text-text-muted">
                {isEmergency
                  ? 'This is an emergency admission, so its type cannot be changed.'
                  : 'Emergency admissions keep their type.'}
              </p>
            )}
          </div>

          <div>
            <label className="mb-1.5 block text-xs font-medium text-text">
              Referred By (Doctor)
            </label>
            <DoctorSearchSelect
              value={referrerId}
              onChange={(value) => setReferrerId(value)}
              placeholder="Select or add outside doctor..."
              disabled={updateAdmission.isPending}
              selectedLabel={referrerLabel}
              showPreview={false}
              onAddNew={(query) => openOutsideDoctor('referrer', query)}
              addNewLabel="Add Outside Doctor"
            />
            {createdLabel(referrerId) && (
              <p className="mt-1 flex items-center gap-1 text-[0.6875rem] text-success">
                <CheckCircle2 size={12} />
                Outside doctor added
              </p>
            )}
            <p className="mt-1.5 text-[0.6875rem] text-text-muted">
              Clear the field to remove the referring doctor. This is a clinical
              attribution — the referral commission is managed separately.
            </p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-border px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={updateAdmission.isPending}
            className="px-4 py-2 text-sm text-text-muted transition-colors hover:text-text disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!hasChanges || updateAdmission.isPending}
            className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-dark disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updateAdmission.isPending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Stethoscope size={16} />
            )}
            Save changes
          </button>
        </div>
      </div>
    </div>

    {/* Quick-entry for a doctor who is not on staff yet. Rendered as a sibling so
        its backdrop click cannot bubble up and close this dialog. */}
    <OutsideDoctorModal
      open={showOutsideDoctorModal}
      initialName={outsideDoctorQuery}
      initialPhone={/^\d+$/.test(outsideDoctorQuery) ? outsideDoctorQuery : ''}
      onClose={() => setShowOutsideDoctorModal(false)}
      onCreated={handleOutsideDoctorCreated}
    />
  </>
  );
}
